/**
 * CR107: Layout Editor Component
 * CR109: Added Typography section with font pickers
 * CR124: Added Header Branding section with title/subtitle/logo from Asset Studio
 * CR125: Improved logo selector with relaxed filtering and quick create flow
 * CR126: Added Header Typography Preset selector
 * CR202: Added header validation and preview indicator
 * 
 * Visual editor for layout presets with drag/drop ordering and block configuration.
 */

import { useState, useCallback, useMemo } from "react";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FontPicker } from "@/components/ui/font-picker";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Eye,
  EyeOff,
  Layout,
  Search,
  Filter,
  ArrowUpDown,
  Grid3X3,
  Star,
  Type,
  Image,
  Palette,
  Plus,
  AlertTriangle,
  AlertCircle,
  Package,
  Minus,  // CR500: Divider block icon
} from "lucide-react";
import type {
  LayoutJson,
  LayoutBlock,
  FilterControl,
  SortOption,
  LayoutBlockId,
  FilterControlId,
  SortOptionId,
  HeaderBlockConfig,
  ResultsBlockConfig,
  ResultsMetaBlockConfig,
  LayoutTypographyConfig,
  CardsViewConfig,
  SearchFilterConfig,
  SearchSuggestionsConfig,
  RecentSearchesConfig,
} from "@/lib/layout-studio/types";
import {
  BLOCK_LABELS,
  FILTER_LABELS,
  SORT_LABELS,
  DEFAULT_HEADER_CONFIG,
  DEFAULT_RESULTS_CONFIG,
  DEFAULT_RESULTS_META_CONFIG,
  DEFAULT_CARDS_VIEW_CONFIG,
  DEFAULT_SEARCH_FILTER_CONFIG,
  DEFAULT_SEARCH_SUGGESTIONS_CONFIG,
  DEFAULT_RECENT_SEARCHES_CONFIG,
} from "@/lib/layout-studio/types";
import { DEFAULT_BODY_FONT_ID, DEFAULT_HEADING_FONT_ID } from "@/lib/theme/fontRegistry";
import { useAppAssets } from "@/hooks/useAppAssets";
import type { AppAsset, AssetType, TintMode } from "@/lib/asset-studio/types";
import { sanitizeSvg } from "@/lib/asset-studio/types";
import { getLogoAssets, isNonIconLogoAsset, NEW_LOGO_ASSET_DEFAULTS } from "@/lib/asset-studio/logoAssetUtils";
import { AssetEditorDialog } from "@/components/admin/asset-studio/AssetEditorDialog";
import {
  HEADER_TYPOGRAPHY_PRESETS,
  DEFAULT_HEADER_TYPOGRAPHY,
  type HeaderTypographyPresetId,
  type HeaderTypographyConfig,
} from "@/lib/layout-studio/headerTypographyPresets";
import { validateHeaderConfig, getHeaderPreviewState } from "@/lib/layout-studio/headerValidation";
import { HeaderPreviewIndicator } from "./HeaderPreviewIndicator";
import { ResultsMetaBlockEditor } from "./ResultsMetaBlockEditor";
import { CardsViewSection } from "./CardsViewSection";
import { PaginationSection } from "./PaginationSection";
import { SearchScopeSection } from "./SearchScopeSection";
import { SearchSuggestionsSection } from "./SearchSuggestionsSection";
import { RecentSearchesSection } from "./RecentSearchesSection";

interface LayoutEditorProps {
  layout: LayoutJson;
  onChange: (layout: LayoutJson) => void;
}

/**
 * CR500: Every block type MUST have an icon in Layout Studio editor.
 */
const BLOCK_ICONS: Record<LayoutBlockId, React.ReactNode> = {
  header: <Layout className="h-4 w-4" />,
  search_filters: <Search className="h-4 w-4" />,
  sort_bar: <ArrowUpDown className="h-4 w-4" />,
  deal_type_filter: <Filter className="h-4 w-4" />,
  results_meta: <Type className="h-4 w-4" />,    // CR500: Results count header
  divider: <Minus className="h-4 w-4" />,        // CR500: Visual separator
  results: <Grid3X3 className="h-4 w-4" />,
  ambiguous_deals: <AlertCircle className="h-4 w-4" />,
  empty_state: <Package className="h-4 w-4" />,
  featured_deals: <Star className="h-4 w-4" />,
};

// ================================================================
// SORTABLE ITEM COMPONENT (CR322: Accordion-based collapsed by default)
// ================================================================

interface SortableItemProps {
  id: string;
  enabled: boolean;
  label: string;
  icon?: React.ReactNode;
  onToggle: (enabled: boolean) => void;
  children?: React.ReactNode;
  /** Optional summary text shown when collapsed */
  collapsedSummary?: string;
}

function SortableItem({ id, enabled, label, icon, onToggle, children, collapsedSummary }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasConfig = Boolean(children);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border bg-card ${
        enabled ? "border-border" : "border-dashed border-muted opacity-60"
      }`}
    >
      {/* CR322: If block has config content, wrap in accordion */}
      {hasConfig && enabled ? (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value={id} className="border-0">
            <div className="flex items-center gap-3 p-3">
              <button
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <AccordionTrigger className="flex-1 py-0 hover:no-underline">
                    <div className="flex items-center gap-2">
                      {icon}
                      <span className="font-medium text-sm">{label}</span>
                    </div>
                  </AccordionTrigger>
                  <Switch
                    checked={enabled}
                    onCheckedChange={onToggle}
                    aria-label={`Toggle ${label}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {/* Optional collapsed summary */}
                {collapsedSummary && (
                  <p className="text-[10px] text-muted-foreground mt-1 truncate">
                    {collapsedSummary}
                  </p>
                )}
              </div>
            </div>
            <AccordionContent className="px-3 pb-3 pt-0">
              <div className="pl-7 pt-3 border-t border-dashed">
                {children}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        /* No config or disabled - simple row without accordion */
        <div className="flex items-center gap-3 p-3">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {icon}
                <span className="font-medium text-sm">{label}</span>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={onToggle}
                aria-label={`Toggle ${label}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================
// MAIN EDITOR
// ================================================================

export function LayoutEditor({ layout, onChange }: LayoutEditorProps) {
  // CR125: Fetch all active assets from Asset Studio (hook handles CRUD)
  const { 
    assets: allAssets, 
    loading: assetsLoading, 
    createAsset,
    saving: assetSaving,
  } = useAppAssets({ 
    status: "active" 
  });
  
  // CR125: Filter and sort to logo-suitable assets using shared utility
  const availableLogoAssets = useMemo(() => {
    return getLogoAssets(allAssets);
  }, [allAssets]);
  
  // CR125: State for quick logo create dialog
  const [showCreateLogoDialog, setShowCreateLogoDialog] = useState(false);

  // CR125: Handle quick logo asset creation from Layout Studio
  const handleCreateLogoAsset = useCallback(async (data: {
    name: string;
    type: AssetType;
    svg: string;
    is_tileable: boolean;
    default_tint: string | null;
    tint_mode: TintMode;
    tags: string[];
  }) => {
    const created = await createAsset(data);
    if (created) {
      // Auto-select the newly created asset as the header logo
      const headerConfig = layout.blocks.find((b) => b.id === "header")?.config as HeaderBlockConfig || DEFAULT_HEADER_CONFIG;
      const newBlocks = layout.blocks.map((b) =>
        b.id === "header"
          ? { ...b, config: { ...headerConfig, logoAssetId: created.id, logoUrl: undefined } }
          : b
      );
      onChange({ ...layout, blocks: newBlocks });
      setShowCreateLogoDialog(false);
    }
  }, [createAsset, layout, onChange]);

  // CR125: Get currently selected asset for warning display
  const selectedLogoAsset = useMemo(() => {
    const headerConfig = layout.blocks.find((b) => b.id === "header")?.config as HeaderBlockConfig;
    if (!headerConfig?.logoAssetId) return null;
    return allAssets.find((a) => a.id === headerConfig.logoAssetId) || null;
  }, [layout, allAssets]);

  // CR202: Header validation and preview state
  const headerValidation = useMemo(() => {
    const headerBlock = layout.blocks.find((b) => b.id === "header");
    const headerConfig = (headerBlock?.config as HeaderBlockConfig) || DEFAULT_HEADER_CONFIG;
    return validateHeaderConfig(headerBlock, headerConfig, allAssets);
  }, [layout.blocks, allAssets]);

  const headerPreviewState = useMemo(() => {
    const headerBlock = layout.blocks.find((b) => b.id === "header");
    const headerConfig = (headerBlock?.config as HeaderBlockConfig) || DEFAULT_HEADER_CONFIG;
    return getHeaderPreviewState(headerBlock, headerConfig, selectedLogoAsset);
  }, [layout.blocks, selectedLogoAsset]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ----------------------------------------------------------------
  // BLOCK HANDLERS
  // ----------------------------------------------------------------

  const handleBlockDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = layout.blocks.findIndex((b) => b.id === active.id);
    const newIndex = layout.blocks.findIndex((b) => b.id === over.id);

    const newBlocks = arrayMove(layout.blocks, oldIndex, newIndex).map((b, i) => ({
      ...b,
      order: i,
    }));

    onChange({ ...layout, blocks: newBlocks });
  }, [layout, onChange]);

  const handleBlockToggle = useCallback((blockId: LayoutBlockId, enabled: boolean) => {
    const newBlocks = layout.blocks.map((b) =>
      b.id === blockId ? { ...b, enabled } : b
    );
    onChange({ ...layout, blocks: newBlocks });
  }, [layout, onChange]);

  const handleBlockConfigChange = useCallback((blockId: LayoutBlockId, config: Record<string, unknown>) => {
    const newBlocks = layout.blocks.map((b) =>
      b.id === blockId ? { ...b, config: { ...b.config, ...config } } : b
    );
    onChange({ ...layout, blocks: newBlocks });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // FILTER HANDLERS
  // ----------------------------------------------------------------

  const handleFilterDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = layout.filters.findIndex((f) => f.id === active.id);
    const newIndex = layout.filters.findIndex((f) => f.id === over.id);

    const newFilters = arrayMove(layout.filters, oldIndex, newIndex).map((f, i) => ({
      ...f,
      order: i,
    }));

    onChange({ ...layout, filters: newFilters });
  }, [layout, onChange]);

  const handleFilterToggle = useCallback((filterId: FilterControlId, enabled: boolean) => {
    const newFilters = layout.filters.map((f) =>
      f.id === filterId ? { ...f, enabled } : f
    );
    onChange({ ...layout, filters: newFilters });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // SORT HANDLERS
  // ----------------------------------------------------------------

  const handleSortDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = layout.sortOptions.findIndex((s) => s.id === active.id);
    const newIndex = layout.sortOptions.findIndex((s) => s.id === over.id);

    const newSorts = arrayMove(layout.sortOptions, oldIndex, newIndex).map((s, i) => ({
      ...s,
      order: i,
    }));

    onChange({ ...layout, sortOptions: newSorts });
  }, [layout, onChange]);

  const handleSortToggle = useCallback((sortId: SortOptionId, enabled: boolean) => {
    const newSorts = layout.sortOptions.map((s) =>
      s.id === sortId ? { ...s, enabled } : s
    );
    onChange({ ...layout, sortOptions: newSorts });
  }, [layout, onChange]);

  const handleDefaultSortChange = useCallback((sortId: SortOptionId) => {
    onChange({ ...layout, defaultSort: sortId });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // RESULTS CONFIG HANDLERS
  // ----------------------------------------------------------------

  const handleResultsConfigChange = useCallback((config: Partial<ResultsBlockConfig>) => {
    onChange({
      ...layout,
      resultsConfig: { ...layout.resultsConfig, ...config },
    });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // CR315: CARDS VIEW HANDLER
  // ----------------------------------------------------------------

  const handleCardsViewChange = useCallback((cardsView: CardsViewConfig) => {
    onChange({
      ...layout,
      resultsConfig: { ...layout.resultsConfig, cardsView },
    });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // CR315: SEARCH FILTER CONFIG HANDLER
  // ----------------------------------------------------------------

  const handleSearchConfigChange = useCallback((searchConfig: SearchFilterConfig) => {
    const newFilters = layout.filters.map((f) =>
      f.id === "search" ? { ...f, config: searchConfig } : f
    );
    onChange({ ...layout, filters: newFilters });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // CR316: SUGGESTIONS CONFIG HANDLER
  // ----------------------------------------------------------------

  const handleSuggestionsConfigChange = useCallback((suggestions: SearchSuggestionsConfig) => {
    const currentConfig = (layout.filters.find((f) => f.id === "search")?.config as SearchFilterConfig) || DEFAULT_SEARCH_FILTER_CONFIG;
    const newFilters = layout.filters.map((f) =>
      f.id === "search" ? { ...f, config: { ...currentConfig, suggestions } } : f
    );
    onChange({ ...layout, filters: newFilters });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // CR316: RECENT SEARCHES CONFIG HANDLER
  // ----------------------------------------------------------------

  const handleRecentSearchesConfigChange = useCallback((recentSearches: RecentSearchesConfig) => {
    const currentConfig = (layout.filters.find((f) => f.id === "search")?.config as SearchFilterConfig) || DEFAULT_SEARCH_FILTER_CONFIG;
    const newFilters = layout.filters.map((f) =>
      f.id === "search" ? { ...f, config: { ...currentConfig, recentSearches } } : f
    );
    onChange({ ...layout, filters: newFilters });
  }, [layout, onChange]);

  // ----------------------------------------------------------------
  // CR109: TYPOGRAPHY HANDLERS
  // ----------------------------------------------------------------

  const handleTypographyChange = useCallback((update: Partial<LayoutTypographyConfig>) => {
    onChange({
      ...layout,
      typography: { ...layout.typography, ...update },
    });
  }, [layout, onChange]);

  // Get current configs
  const headerBlock = layout.blocks.find((b) => b.id === "header");
  const headerConfig = (headerBlock?.config as HeaderBlockConfig) || DEFAULT_HEADER_CONFIG;
  
  // CR500: Get results_meta config for summary
  const resultsMetaBlock = layout.blocks.find((b) => b.id === "results_meta");
  const resultsMetaConfig = (resultsMetaBlock?.config as ResultsMetaBlockConfig) || DEFAULT_RESULTS_META_CONFIG;

  // CR315: Get cards view config
  const cardsViewConfig = layout.resultsConfig.cardsView || DEFAULT_CARDS_VIEW_CONFIG;

  // CR315: Get search filter config
  const searchFilter = layout.filters.find((f) => f.id === "search");
  const searchConfig = (searchFilter?.config as SearchFilterConfig) || DEFAULT_SEARCH_FILTER_CONFIG;

  const sortedBlocks = [...layout.blocks].sort((a, b) => a.order - b.order);
  const sortedFilters = [...layout.filters].sort((a, b) => a.order - b.order);
  const sortedSorts = [...layout.sortOptions].sort((a, b) => a.order - b.order);
  const typography = layout.typography ?? {};
  
  // CR322: Generate collapsed summaries for blocks with config
  const getBlockSummary = useCallback((blockId: LayoutBlockId): string | undefined => {
    switch (blockId) {
      case "header": {
        const parts: string[] = [];
        if (headerConfig.showTitle && headerConfig.titleText) {
          parts.push(`Title: ${headerConfig.titleText}`);
        }
        if (headerConfig.showLogo) {
          parts.push("Logo: On");
        }
        return parts.length > 0 ? parts.join(" • ") : undefined;
      }
      case "results_meta": {
        const parts: string[] = [];
        if (resultsMetaConfig.showCount) parts.push("Count: On");
        if (resultsMetaConfig.labelText) parts.push(`Label: "${resultsMetaConfig.labelText}"`);
        return parts.length > 0 ? parts.join(" • ") : undefined;
      }
      default:
        return undefined;
    }
  }, [headerConfig, resultsMetaConfig]);

  return (
    <ScrollArea className="h-[600px]">
      <div className="space-y-6 pr-4">
        {/* CR109: Typography Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Type className="h-4 w-4" />
              Typography
            </CardTitle>
            <CardDescription>
              Choose fonts for the page UI and titles.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">UI Font</Label>
                <FontPicker
                  value={typography.uiFontId ?? DEFAULT_BODY_FONT_ID}
                  onChange={(fontId) => handleTypographyChange({ uiFontId: fontId })}
                  size="sm"
                  showPreview
                />
                <p className="text-[10px] text-muted-foreground">
                  Used for body text and interface elements
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Title Font</Label>
                <FontPicker
                  value={typography.titleFontId ?? DEFAULT_HEADING_FONT_ID}
                  onChange={(fontId) => handleTypographyChange({ titleFontId: fontId })}
                  size="sm"
                  showPreview
                />
                <p className="text-[10px] text-muted-foreground">
                  Used for page titles and headings
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Page Blocks */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Layout className="h-4 w-4" />
              Page Blocks
            </CardTitle>
            <CardDescription>
              Drag to reorder. Toggle to show/hide blocks.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleBlockDragEnd}
            >
              <SortableContext
                items={sortedBlocks.map((b) => b.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {sortedBlocks.map((block) => (
                    <SortableItem
                      key={block.id}
                      id={block.id}
                      enabled={block.enabled}
                      label={BLOCK_LABELS[block.id]}
                      icon={BLOCK_ICONS[block.id]}
                      onToggle={(enabled) => handleBlockToggle(block.id, enabled)}
                      collapsedSummary={getBlockSummary(block.id)}
                    >
                    {/* Header config - CR124: expanded branding section, CR202: added preview indicator */}
                      {block.id === "header" && (
                        <div className="space-y-4">
                          {/* CR202: Header Preview Indicator */}
                          <HeaderPreviewIndicator
                            enabled={headerPreviewState.enabled}
                            hasLogo={headerPreviewState.hasLogo}
                            hasTitle={headerPreviewState.hasTitle}
                            hasSubtitle={headerPreviewState.hasSubtitle}
                            effectiveTitle={headerPreviewState.effectiveTitle}
                            effectiveSubtitle={headerPreviewState.effectiveSubtitle}
                            logoAsset={selectedLogoAsset}
                            validation={headerValidation}
                          />
                          
                          {/* CR124: Branding Section */}
                          <div className="space-y-3">
                            <Label className="text-xs font-medium flex items-center gap-1.5">
                              <Palette className="h-3 w-3" />
                              Branding
                            </Label>
                            
                            {/* Title Text */}
                            <div className="space-y-1">
                              <Label className="text-[10px] text-muted-foreground">Title Text</Label>
                              <Input
                                value={headerConfig.titleText || "Deal Finder"}
                                onChange={(e) =>
                                  handleBlockConfigChange("header", { titleText: e.target.value })
                                }
                                placeholder="Deal Finder"
                                className="h-7 text-xs"
                              />
                            </div>
                            
                            {/* Subtitle Text */}
                            <div className="space-y-1">
                              <Label className="text-[10px] text-muted-foreground">Subtitle Text</Label>
                              <Input
                                value={headerConfig.subtitleText || "Find the best gift card deals"}
                                onChange={(e) =>
                                  handleBlockConfigChange("header", { subtitleText: e.target.value })
                                }
                                placeholder="Find the best gift card deals"
                                className="h-7 text-xs"
                              />
                            </div>
                            
                            {/* CR125: Logo Source - Asset Studio (Improved) */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-[10px] text-muted-foreground flex items-center gap-1">
                                  <Image className="h-3 w-3" />
                                  Logo (from Asset Studio)
                                </Label>
                                {/* CR125: Quick create button always visible */}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowCreateLogoDialog(true)}
                                  className="h-5 px-2 text-[10px] gap-1"
                                >
                                  <Plus className="h-3 w-3" />
                                  Create
                                </Button>
                              </div>
                              
                              {/* CR125: Show selector if assets available, or helpful empty state */}
                              {availableLogoAssets.length > 0 ? (
                                <>
                                  <Select
                                    value={headerConfig.logoAssetId || "_none_"}
                                    onValueChange={(v) =>
                                      handleBlockConfigChange("header", { 
                                        logoAssetId: v === "_none_" ? undefined : v,
                                        logoUrl: undefined
                                      })
                                    }
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue placeholder="Select logo asset..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="_none_">
                                        <span className="text-muted-foreground">Default (no custom logo)</span>
                                      </SelectItem>
                                      {availableLogoAssets.map((asset) => (
                                        <SelectItem key={asset.id} value={asset.id}>
                                          <div className="flex items-center gap-2">
                                            {/* CR201/CR282: Use img with resolved URL */}
                                            <img 
                                              src={getAssetImageSrc(asset) || ""}
                                              alt={asset.name}
                                              className="w-4 h-4 flex-shrink-0 object-contain"
                                            />
                                            <span>{asset.name}</span>
                                            {asset.tags?.includes("logo") && (
                                              <Badge variant="secondary" className="text-[8px] px-1 py-0">logo</Badge>
                                            )}
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  
                                  {/* CR125: Warning for non-icon assets selected as logo */}
                                  {selectedLogoAsset && isNonIconLogoAsset(selectedLogoAsset) && (
                                    <Alert variant="default" className="py-1.5 px-2">
                                      <AlertTriangle className="h-3 w-3" />
                                      <AlertDescription className="text-[10px]">
                                        This asset isn't an Icon. It may not scale as expected.
                                      </AlertDescription>
                                    </Alert>
                                  )}
                                </>
                              ) : assetsLoading ? (
                                <p className="text-[10px] text-muted-foreground">Loading assets...</p>
                              ) : (
                                /* CR125: Improved empty state with actionable options */
                                <div className="border border-dashed rounded-md p-3 space-y-2 bg-muted/30">
                                  <p className="text-[10px] text-muted-foreground text-center">
                                    No logo assets available yet.
                                  </p>
                                  <div className="flex gap-2 justify-center">
                                    <Button
                                      type="button"
                                      variant="default"
                                      size="sm"
                                      onClick={() => setShowCreateLogoDialog(true)}
                                      className="h-6 text-[10px] gap-1"
                                    >
                                      <Plus className="h-3 w-3" />
                                      Create Logo Asset
                                    </Button>
                                  </div>
                                  <p className="text-[9px] text-muted-foreground text-center">
                                    Or add an Icon asset in Asset Studio with a "logo" tag.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <Separator />
                          
                          {/* CR126: Typography Preset Section */}
                          <div className="space-y-2">
                            <Label className="text-xs font-medium flex items-center gap-1.5">
                              <Type className="h-3 w-3" />
                              Typography
                            </Label>
                            <Select
                              value={headerConfig.headerTypography?.presetId || "default"}
                              onValueChange={(v) =>
                                handleBlockConfigChange("header", {
                                  headerTypography: {
                                    ...(headerConfig.headerTypography || DEFAULT_HEADER_TYPOGRAPHY),
                                    presetId: v as HeaderTypographyPresetId,
                                  },
                                })
                              }
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue placeholder="Select typography preset..." />
                              </SelectTrigger>
                              <SelectContent>
                                {HEADER_TYPOGRAPHY_PRESETS.map((preset) => (
                                  <SelectItem key={preset.id} value={preset.id}>
                                    <div className="flex flex-col">
                                      <span>{preset.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-[10px] text-muted-foreground">
                              Controls font size and weight for the Deal Finder title and subtitle.
                            </p>
                          </div>
                          
                          <Separator />
                          
                          {/* Visibility & Layout toggles */}
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">Show Logo</Label>
                              <Switch
                                checked={headerConfig.showLogo}
                                onCheckedChange={(v) =>
                                  handleBlockConfigChange("header", { showLogo: v })
                                }
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">Show Title</Label>
                              <Switch
                                checked={headerConfig.showTitle}
                                onCheckedChange={(v) =>
                                  handleBlockConfigChange("header", { showTitle: v })
                                }
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">Show Subtitle</Label>
                              <Switch
                                checked={headerConfig.showSubtitle}
                                onCheckedChange={(v) =>
                                  handleBlockConfigChange("header", { showSubtitle: v })
                                }
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Alignment</Label>
                              <Select
                                value={headerConfig.alignment}
                                onValueChange={(v) =>
                                  handleBlockConfigChange("header", { alignment: v })
                                }
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="left">Left</SelectItem>
                                  <SelectItem value="center">Center</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Logo Size</Label>
                              <Select
                                value={headerConfig.logoSize}
                                onValueChange={(v) =>
                                  handleBlockConfigChange("header", { logoSize: v })
                                }
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sm">Small</SelectItem>
                                  <SelectItem value="md">Medium</SelectItem>
                                  <SelectItem value="lg">Large</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* CR283: Results Meta (Count) Config */}
                      {block.id === "results_meta" && (
                        <ResultsMetaBlockEditor
                          config={(block.config as ResultsMetaBlockConfig) || DEFAULT_RESULTS_META_CONFIG}
                          onChange={(config) => handleBlockConfigChange("results_meta", config)}
                        />
                      )}
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>

        {/* Filter Controls */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filter Controls
            </CardTitle>
            <CardDescription>
              Configure which filters appear and in what order.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFilterDragEnd}
            >
              <SortableContext
                items={sortedFilters.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {sortedFilters.map((filter) => (
                    <SortableItem
                      key={filter.id}
                      id={filter.id}
                      enabled={filter.enabled}
                      label={FILTER_LABELS[filter.id]}
                      onToggle={(enabled) => handleFilterToggle(filter.id, enabled)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>

        {/* Sort Options */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4" />
              Sort Options
            </CardTitle>
            <CardDescription>
              Configure sort options and default sort.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">Default Sort</Label>
                <Select
                  value={layout.defaultSort}
                  onValueChange={(v) => handleDefaultSortChange(v as SortOptionId)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedSorts
                      .filter((s) => s.enabled)
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {SORT_LABELS[s.id]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSortDragEnd}
              >
                <SortableContext
                  items={sortedSorts.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {sortedSorts.map((sort) => (
                      <SortableItem
                        key={sort.id}
                        id={sort.id}
                        enabled={sort.enabled}
                        label={SORT_LABELS[sort.id]}
                        onToggle={(enabled) => handleSortToggle(sort.id, enabled)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </CardContent>
        </Card>

        {/* Results Configuration */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Grid3X3 className="h-4 w-4" />
              Results Display
            </CardTitle>
            <CardDescription>
              Configure how deal results are displayed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Layout Type</Label>
                <Select
                  value={layout.resultsConfig.layoutType}
                  onValueChange={(v) =>
                    handleResultsConfigChange({ layoutType: v as "grid" | "list" })
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grid">Grid</SelectItem>
                    <SelectItem value="list">List</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Density</Label>
                <Select
                  value={layout.resultsConfig.density}
                  onValueChange={(v) =>
                    handleResultsConfigChange({ density: v as "compact" | "comfortable" })
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="comfortable">Comfortable</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Pagination</Label>
                <Select
                  value={layout.resultsConfig.pagination}
                  onValueChange={(v) =>
                    handleResultsConfigChange({ pagination: v as "pages" | "infinite" })
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pages">Pages</SelectItem>
                    <SelectItem value="infinite">Infinite Scroll</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Page Size</Label>
                <Select
                  value={String(layout.resultsConfig.pageSize)}
                  onValueChange={(v) =>
                    handleResultsConfigChange({ pageSize: Number(v) })
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6</SelectItem>
                    <SelectItem value="12">12</SelectItem>
                    <SelectItem value="24">24</SelectItem>
                    <SelectItem value="48">48</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div>
              <Label className="text-xs">Grid Columns by Breakpoint</Label>
              <div className="grid grid-cols-3 gap-4 mt-2">
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Desktop</span>
                  <Select
                    value={String(layout.resultsConfig.gridColumns.desktop)}
                    onValueChange={(v) =>
                      handleResultsConfigChange({
                        gridColumns: { ...layout.resultsConfig.gridColumns, desktop: Number(v) },
                      })
                    }
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} col{n > 1 ? "s" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Tablet</span>
                  <Select
                    value={String(layout.resultsConfig.gridColumns.tablet)}
                    onValueChange={(v) =>
                      handleResultsConfigChange({
                        gridColumns: { ...layout.resultsConfig.gridColumns, tablet: Number(v) },
                      })
                    }
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} col{n > 1 ? "s" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground">Mobile</span>
                  <Select
                    value={String(layout.resultsConfig.gridColumns.mobile)}
                    onValueChange={(v) =>
                      handleResultsConfigChange({
                        gridColumns: { ...layout.resultsConfig.gridColumns, mobile: Number(v) },
                      })
                    }
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} col{n > 1 ? "s" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CR315: Cards View Configuration */}
        <CardsViewSection
          config={cardsViewConfig}
          onChange={handleCardsViewChange}
        />

        {/* CR315: Pagination Configuration */}
        <PaginationSection
          pagination={layout.resultsConfig.pagination}
          pageSize={layout.resultsConfig.pageSize}
          onChange={handleResultsConfigChange}
        />

        {/* CR315: Search Scope Configuration */}
        <SearchScopeSection
          config={searchConfig}
          onChange={handleSearchConfigChange}
        />

        {/* CR316: Search Suggestions Configuration */}
        <SearchSuggestionsSection
          config={searchConfig.suggestions || DEFAULT_SEARCH_SUGGESTIONS_CONFIG}
          onChange={handleSuggestionsConfigChange}
        />

        {/* CR316: Recent Searches Configuration */}
        <RecentSearchesSection
          config={searchConfig.recentSearches || DEFAULT_RECENT_SEARCHES_CONFIG}
          onChange={handleRecentSearchesConfigChange}
        />
      </div>
      
      {/* CR125: Quick Logo Create Dialog */}
      <AssetEditorDialog
        open={showCreateLogoDialog}
        onOpenChange={setShowCreateLogoDialog}
        asset={null}
        onSave={handleCreateLogoAsset}
        saving={assetSaving}
        initialDefaults={NEW_LOGO_ASSET_DEFAULTS}
      />
    </ScrollArea>
  );
}
